import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getNotifications,
  createNotification,
  bulkCreateNotifications,
  deleteNotification,
} from "../../../data";
import { AdminLayout } from "../shared/layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import {
  Bell,
  Plus,
  Trash2,
  Users,
  Send,
  Info,
  AlertTriangle,
  CheckCircle,
  XCircle,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { tr } from "date-fns/locale";

export function NotificationsScreen() {
  const queryClient = useQueryClient();
  const [notifyDialog, setNotifyDialog] = useState({ open: false, bulk: false });
  const [formData, setFormData] = useState({
    userId: "",
    userIds: "",
    title: "",
    message: "",
    type: "INFO",
    actionUrl: "",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: getNotifications,
  });

  const createMutation = useMutation({
    mutationFn: createNotification,
    onSuccess: () => {
      queryClient.invalidateQueries(["notifications"]);
      setNotifyDialog({ open: false, bulk: false });
      resetForm();
      toast({
        title: "Başarılı",
        description: "Bildirim gönderildi",
        variant: "success",
      });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "İşlem başarısız",
        variant: "destructive",
      });
    },
  });

  const bulkCreateMutation = useMutation({
    mutationFn: (data) => bulkCreateNotifications(
      data.userIds,
      data.title,
      data.message,
      data.type,
      data.actionUrl
    ),
    onSuccess: () => {
      queryClient.invalidateQueries(["notifications"]);
      setNotifyDialog({ open: false, bulk: false });
      resetForm();
      toast({
        title: "Başarılı",
        description: "Toplu bildirim gönderildi",
        variant: "success",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteNotification,
    onSuccess: () => {
      queryClient.invalidateQueries(["notifications"]);
      toast({
        title: "Başarılı",
        description: "Bildirim silindi",
        variant: "success",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      userId: "",
      userIds: "",
      title: "",
      message: "",
      type: "INFO",
      actionUrl: "",
    });
  };

  const handleSubmit = () => {
    if (notifyDialog.bulk) {
      const userIds = formData.userIds.split(",").map(id => id.trim()).filter(Boolean);
      bulkCreateMutation.mutate({
        userIds,
        title: formData.title,
        message: formData.message,
        type: formData.type,
        actionUrl: formData.actionUrl || undefined,
      });
    } else {
      createMutation.mutate({
        userId: parseInt(formData.userId),
        title: formData.title,
        message: formData.message,
        type: formData.type,
        actionUrl: formData.actionUrl || undefined,
      });
    }
  };

  const getTypeBadge = (type) => {
    const config = {
      INFO: { variant: "default", icon: Info, label: "Info" },
      SUCCESS: { variant: "success", icon: CheckCircle, label: "Success" },
      WARNING: { variant: "warning", icon: AlertTriangle, label: "Warning" },
      ERROR: { variant: "destructive", icon: XCircle, label: "Error" },
    };

    const { variant, icon: Icon, label } = config[type] || config.INFO;

    return (
      <Badge variant={variant} className="flex items-center w-fit gap-1">
        <Icon className="h-3 w-3" />
        {label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "dd MMM yyyy, HH:mm", { locale: tr });
    } catch (error) {
      return dateString;
    }
  };

  const notifications = data?.data?.data || [];

  const actions = [
    {
      name: "Tek Kullanıcıya Gönder",
      icon: <Plus className="mr-2 h-4 w-4" />,
      onClick: () => {
        setNotifyDialog({ open: true, bulk: false });
        resetForm();
      },
    },
    {
      name: "Toplu Gönder",
      icon: <Users className="mr-2 h-4 w-4" />,
      onClick: () => {
        setNotifyDialog({ open: true, bulk: true });
        resetForm();
      },
    },
  ];

  return (
    <AdminLayout
      title="Notifications"
      description="Kullanıcılara bildirim gönderin"
      loading={isLoading}
      actions={actions}
    >
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Bell className="mr-2 h-5 w-5" />
            Bildirimler ({notifications.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {notifications.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Bell className="mx-auto h-12 w-12 mb-4 text-gray-300" />
              <p>Henüz bildirim bulunmuyor.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Kullanıcı</TableHead>
                  <TableHead>Başlık</TableHead>
                  <TableHead>Mesaj</TableHead>
                  <TableHead>Tip</TableHead>
                  <TableHead>Okundu</TableHead>
                  <TableHead>Tarih</TableHead>
                  <TableHead>İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {notifications.map((notification) => (
                  <TableRow key={notification.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{notification.user?.name || "N/A"}</p>
                        <p className="text-xs text-gray-500">{notification.user?.email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{notification.title}</TableCell>
                    <TableCell>
                      <div className="max-w-xs truncate text-sm">
                        {notification.message}
                      </div>
                    </TableCell>
                    <TableCell>{getTypeBadge(notification.type)}</TableCell>
                    <TableCell>
                      {notification.read ? (
                        <Badge variant="success">Okundu</Badge>
                      ) : (
                        <Badge variant="secondary">Okunmadı</Badge>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(notification.createdAt)}</TableCell>
                    <TableCell>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Bildirimi Sil</AlertDialogTitle>
                            <AlertDialogDescription>
                              Bu bildirimi silmek istediğinizden emin misiniz?
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>İptal</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteMutation.mutate(notification.id)}
                            >
                              Sil
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Notification Dialog */}
      <Dialog
        open={notifyDialog.open}
        onOpenChange={(open) => {
          setNotifyDialog({ ...notifyDialog, open });
          if (!open) resetForm();
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {notifyDialog.bulk ? "Toplu Bildirim Gönder" : "Bildirim Gönder"}
            </DialogTitle>
            <DialogDescription>
              {notifyDialog.bulk
                ? "Birden fazla kullanıcıya bildirim gönderin"
                : "Tek bir kullanıcıya bildirim gönderin"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {notifyDialog.bulk ? (
              <div className="space-y-2">
                <Label htmlFor="userIds">User IDs * (virgülle ayırın)</Label>
                <Input
                  id="userIds"
                  placeholder="1, 2, 3, 4"
                  value={formData.userIds}
                  onChange={(e) => setFormData({ ...formData, userIds: e.target.value })}
                />
                <p className="text-xs text-gray-500">
                  Örnek: 1, 2, 3 (virgülle ayırarak)
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="userId">User ID *</Label>
                <Input
                  id="userId"
                  type="number"
                  placeholder="1"
                  value={formData.userId}
                  onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="title">Başlık *</Label>
              <Input
                id="title"
                placeholder="Bildirim başlığı"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Mesaj *</Label>
              <Textarea
                id="message"
                placeholder="Bildirim mesajı..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Tip</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="INFO">Info</SelectItem>
                  <SelectItem value="SUCCESS">Success</SelectItem>
                  <SelectItem value="WARNING">Warning</SelectItem>
                  <SelectItem value="ERROR">Error</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="actionUrl">Action URL (Opsiyonel)</Label>
              <Input
                id="actionUrl"
                placeholder="/profile"
                value={formData.actionUrl}
                onChange={(e) => setFormData({ ...formData, actionUrl: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setNotifyDialog({ open: false, bulk: false });
                resetForm();
              }}
            >
              İptal
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                !formData.title ||
                !formData.message ||
                (!notifyDialog.bulk && !formData.userId) ||
                (notifyDialog.bulk && !formData.userIds) ||
                createMutation.isLoading ||
                bulkCreateMutation.isLoading
              }
            >
              <Send className="mr-2 h-4 w-4" />
              {createMutation.isLoading || bulkCreateMutation.isLoading
                ? "Gönderiliyor..."
                : "Gönder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

